import {HttpBackend, HttpClient} from '@angular/common/http';
import {Inject, Injectable, Optional} from '@angular/core';
import {BehaviorSubject, Observable, catchError, lastValueFrom, map, of, tap, throwError} from 'rxjs';
import {AGENT_AUTH_BASE_URL, AGENT_AUTH_REALM} from './agent-auth.config';
import {AuthService, ChangePasswordRequest, User} from './auth.service';

export interface AgentTokenPayload {
  accessToken: string;
  refreshToken: string;
  expiredAt: string;
}

export interface AgentTokenResponse {
  message: string;
  body: AgentTokenPayload;
}

export interface AgentAccount {
  id: string;
  externalUserId: string;
  agentId: string;
}

export interface AgentChangePasswordResponse {
  agentAccount: AgentAccount;
}

interface AgentJwtClaims {
  userId?: string;
  UserId?: string;
  uid?: string;
  sub?: string;
  realm?: string;
  Realm?: string;
  exp?: number;
}

type StoredTokens = AgentTokenPayload & {
  updatedAt: string;
};

@Injectable()
export class AgentAuthService implements AuthService {
  private readonly rawHttp: HttpClient;
  private readonly userSubject = new BehaviorSubject<User | null>(null);
  private refreshPromise?: Promise<string | undefined>;
  private readonly storageKey = 'agent-auth-tokens';
  private readonly refreshWindowMs = 30 * 1000;

  constructor(
    private http: HttpClient,
    backend: HttpBackend,
    @Inject(AGENT_AUTH_BASE_URL) private baseUrl: string,
    @Optional() @Inject(AGENT_AUTH_REALM) private expectedRealm?: string,
  ) {
    this.rawHttp = new HttpClient(backend);
    this.restoreFromStorage();
  }

  login(username: string, password: string): Observable<string> {
    return this.rawHttp.post<AgentTokenResponse>(
        `${this.baseUrl}/remitz/api/v0/public/agent/login`,
        {username, password},
    ).pipe(
        tap((res) => this.saveTokens(res.body)),
        tap((res) => this.userSubject.next(this.buildUser(res.body.accessToken))),
        map(() => 'Success Login'),
    );
  }

  loginWithGoogle(): Observable<string> {
    return throwError(() => new Error('Agent auth does not support Google login'));
  }

  logout(): void {
    this.clearTokens();
    this.userSubject.next(null);
  }

  async getToken(): Promise<string | undefined> {
    const tokens = this.readTokens();
    if (!tokens?.accessToken) return undefined;

    const expiresAt = this.getExpiry(tokens) ??
      this.getTokenExpiry(tokens.accessToken);

    if (!expiresAt || this.isExpired(expiresAt)) {
      return await this.refreshAccessToken(tokens.refreshToken);
    }

    return tokens.accessToken;
  }

  authState(): Observable<User | null> {
    return this.userSubject.asObservable();
  }

  changePassword(req: ChangePasswordRequest): Observable<AgentChangePasswordResponse> {
    if (!req.newPassword) {
      return throwError(() => new Error('New password may not be empty'));
    }
    if (req.newPassword !== req.newPasswordConfirm) {
      return throwError(() => new Error('New Password Confirmation not same'));
    }

    return this.http.post<AgentChangePasswordResponse>(
        `${this.baseUrl}/remitz/api/v0/agent/agent/account/change-password`,
        {
          oldPassword: req.oldPassword,
          newPassword: req.newPassword,
          confirmPassword: req.newPasswordConfirm,
        },
    );
  }

  private restoreFromStorage() {
    const tokens = this.readTokens();
    if (!tokens) return;

    const expiresAt = this.getExpiry(tokens) ??
      this.getTokenExpiry(tokens.accessToken);

    if (!expiresAt || this.isExpired(expiresAt)) {
      if (tokens.refreshToken) {
        this.refreshAccessToken(tokens.refreshToken).then((token) => {
          if (token) {
            this.userSubject.next(this.buildUser(token));
          }
        });
      }
      return;
    }

    const user = this.buildUser(tokens.accessToken);
    this.userSubject.next(user);
  }

  private saveTokens(payload: AgentTokenPayload) {
    const toStore: StoredTokens = {
      ...payload,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(this.storageKey, JSON.stringify(toStore));
  }

  private readTokens(): StoredTokens | null {
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as StoredTokens;
    } catch {
      return null;
    }
  }

  private clearTokens() {
    localStorage.removeItem(this.storageKey);
  }

  private async refreshAccessToken(refreshToken?: string): Promise<string | undefined> {
    if (!refreshToken) {
      this.clearTokens();
      this.userSubject.next(null);
      return undefined;
    }

    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refresh$ = this.rawHttp.post<AgentTokenResponse>(
        `${this.baseUrl}/remitz/api/v0/public/agent/refresh-token`,
        {refreshToken},
    ).pipe(
        tap((res) => this.saveTokens(res.body)),
        tap((res) => this.userSubject.next(this.buildUser(res.body.accessToken))),
        map((res) => res.body.accessToken),
        catchError(() => {
          this.clearTokens();
          this.userSubject.next(null);
          return of(undefined);
        }),
    );

    this.refreshPromise = lastValueFrom(refresh$).finally(() => {
      this.refreshPromise = undefined;
    });

    return this.refreshPromise;
  }

  private buildUser(token: string): User | null {
    const claims = this.decodeClaims(token);
    if (!claims) return null;

    const realm = claims.realm ?? claims.Realm ?? '';
    if (this.expectedRealm && realm !== this.expectedRealm) {
      return null;
    }

    const uid =
      claims.userId ?? claims.UserId ?? claims.uid ?? claims.sub ?? '';

    if (!uid) return null;

    return {
      uid,
      email: '',
      name: '',
      emailVerified: false,
      manager: false,
      staff: true,
      idpAgentCode: realm,
      permissions: [],
    };
  }

  private decodeClaims(token: string): AgentJwtClaims | null {
    const parts = token.split('.');
    if (parts.length < 2) return null;

    const payload = parts[1];
    const decoded = this.decodeBase64Url(payload);
    if (!decoded) return null;

    try {
      return JSON.parse(decoded) as AgentJwtClaims;
    } catch {
      return null;
    }
  }

  private resolveAccountId(): string | null {
    const current = this.userSubject.value?.uid;
    if (current) return current;

    const tokens = this.readTokens();
    if (!tokens?.accessToken) return null;

    const claims = this.decodeClaims(tokens.accessToken);
    return claims?.userId ?? claims?.UserId ?? claims?.uid ?? claims?.sub ?? null;
  }

  private decodeBase64Url(input: string): string | null {
    let value = input.replace(/-/g, '+').replace(/_/g, '/');
    const pad = value.length % 4;
    if (pad) {
      value = value.padEnd(value.length + (4 - pad), '=');
    }

    if (typeof atob === 'function') {
      return atob(value);
    }

    const buffer = (globalThis as any).Buffer;
    if (buffer?.from) {
      return buffer.from(value, 'base64').toString('utf8');
    }

    return null;
  }

  private getTokenExpiry(token: string): Date | null {
    const claims = this.decodeClaims(token);
    if (!claims?.exp) return null;
    return new Date(claims.exp * 1000);
  }

  private getExpiry(tokens: StoredTokens): Date | null {
    const parsed = Date.parse(tokens.expiredAt);
    if (Number.isNaN(parsed)) return null;
    return new Date(parsed);
  }

  private isExpired(expiry: Date): boolean {
    return Date.now() >= expiry.getTime() - this.refreshWindowMs;
  }
}
